/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import {
    PageBody,
    Divider,
    Breadcrumbs,
    SingleColumnContainer,
    Heading,
} from 'sarsaparilla';
import { LOTTERY_IMG_PREFIX } from '../constants';

const breadcrumbs = [
    {
        title: 'Home',
        url: '/',
    },
    {
        title: 'Enter Lottery',
        url: '/lottery/available',
    },
    {
        title: 'How does the lottery work?',
    },
];

export default function HowLotteriesWorkPage() {
    return (
        <PageBody>
            <SingleColumnContainer>
                <Breadcrumbs path={breadcrumbs} />
                <Heading headingLevel={1} appearance="h3" className="mb-4">
                    How does the lottery work?
                </Heading>

                <Heading headingLevel={2} appearance="h5">
                    Why does {process.env.SITE_NAME} need lotteries?
                </Heading>
                <p>
                    Everyone deserves an equal opportunity to experience our National
                    treasures! Our lottery system ensures a fair distribution process to
                    provide access to popular recreation opportunities. Limiting the
                    number of visitors through a lottery process helps preserve and
                    protect our beloved locations, prevent damage to the natural landscape
                    and enhance the experience.
                </p>

                <Divider />

                <Heading headingLevel={2} appearance="h5">
                    How does a lottery work?
                </Heading>
                <p>
                    First, you need a {process.env.SITE_NAME} account to participate in a
                    lottery. You can sign up clicking on the Sign Up/Log In button located
                    in the header to create an account.
                </p>
                <p>
                    Second, most lotteries have an application fee to register for the
                    lottery. The fee is charged to each applicant to help manage the
                    lottery process, regardless of if they are selected for that lottery.
                </p>
                <p>
                    Third, be aware of when a lottery application period is opening for
                    your activity. You can see the year-in-a-glance here for the various
                    different lotteries, and be sure to monitor this page to find
                    information about lotteries that are Coming Soon.
                </p>
                <p>
                    Finally, click on the &#34;Enter Lottery&#34; button and enter your
                    application information to be in the running for your permit or
                    ticket.
                </p>
                <p>
                    All applicants receive an email announcing the lottery results and
                    indicating whether they were successful or not. If you aren&#39;t
                    selected, please keep trying. If you are selected for the lottery,
                    make sure to review all the details outlined in the email and follow
                    any additional steps that are included in the award announcement.
                    Also, check the &#34;Need to Know&#34; information on the lottery page
                    prior to visiting the location.
                </p>

                <Divider />

                <Heading headingLevel={2} appearance="h5">
                    How random is the lottery process?
                </Heading>
                <p>
                    Each lottery is randomized by shuffling all the applications using the
                    Fisher-Yates Shuffle, which produces an unbiased and random ordering
                    of results. We also use a Cryptographically Secure Pseudo Random
                    Number Generator (CSPRNG) to prevent any inadvertent bias in the
                    lottery process. These random number generators are vetted to produce
                    random numbers that cannot be predicted based upon past outputs, and
                    they don’t allow anyone to predict future or past numbers generated.
                </p>
                <div className="lottery-heatmap">
                    <picture>
                        <img
                            src={`${LOTTERY_IMG_PREFIX}lotteryStatsHeatmap.png`}
                            alt="How random is the lottery chart"
                            aria-label="Plot of number of times an application is given a particular position in a lottery. The numbers are
                relatively even accross all applications and positions. Additional information about this chart is in the paragraph below."
                            loading="lazy"
                        />
                    </picture>
                </div>
                <p>
                    In the figure above, each row is an application, each column is the
                    position the application was awarded in the lottery (from first on the
                    left to one hundredth on the right), and the color in each cell
                    indicating the number of lotteries that application row was awarded
                    the column&apos;s position. That the plot shows approximately similar
                    counts for each cell is indicative that the lottery is unbiased, as
                    each application receives an award a similar number of times. The plot
                    does not show the exact same color across all cells because the
                    results occurring exactly the same way time after time is highly
                    unlikely; therefore, a truly random outcome likely has each
                    application getting the same position a varying number of times.
                </p>

                <Divider />

                <Heading headingLevel={2} appearance="h5">
                    How can I learn more about lottery statistics?
                </Heading>
                <p>
                    If you&#39;re interested in learning more about lottery statistics,
                    many, but not all, lotteries post this information after the lottery
                    has closed. These statistics often include the total number of
                    submitted applications and details about demand information for the
                    number of applications submitted for specific entry dates, the number
                    of applications received by state and country, and the breakdown of
                    successful applicants by other various criteria.
                </p>

                <p>
                    Learn more about the{' '}
                    <a
                        href="https://www.fs.usda.gov/detail/scnf/passes-permits/recreation/?cid=fsbdev3_029568"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Four Rivers Lottery and Permit Reservation System
                    </a>
                </p>
            </SingleColumnContainer>
        </PageBody>
    );
}
